import {
  format,
  parse,
  parseISO,
  isToday,
  intervalToDuration,
  formatDuration,
} from 'date-fns';
import { es } from 'date-fns/locale';

export function parseDate(date: string, formatString = 'yyyy-MM-dd'): Date {
  return parse(date, formatString, new Date());
}

export function formatDate(
  date: Date | undefined,
  formatString = 'yyyy-MM-dd'
): string {
  if (!date) return '';
  return format(date, formatString);
}

export function formatWithLocale(
  date: string,
  formatString = 'yyyy-MM-dd'
): string {
  return format(parseISO(date), formatString, {
    locale: es,
  });
}

export function formatISODate(
  date: string,
  formatString = 'dd/MM/yyyy',
  defaultText = 'No declarada'
): string {
  if (!date) {
    return defaultText;
  }
  return formatDate(parseISO(date), formatString);
}

export function formatTimelineDateParts(
  date: string,
  defaultText = ''
): { date: string; time?: string } {
  if (!date) {
    return { date: defaultText };
  }
  const ymd = date.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return { date: defaultText };
  }
  const [year, month, day] = ymd.split('-');
  const civilDate = `${day}/${month}/${year}`;
  const time = date.match(/T(\d{2}:\d{2})/)?.[1];
  return time ? { date: civilDate, time } : { date: civilDate };
}

export function formatTimelineInstant(date: string, defaultText = ''): string {
  const parts = formatTimelineDateParts(date, defaultText);
  return parts.time ? `${parts.date} ${parts.time}` : parts.date;
}

function timelineCivilDate(date: string): Date | null {
  const ymd = date.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return null;
  }
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

export function formatDayDelta(from: string, to: string): string | null {
  const start = timelineCivilDate(from);
  const end = timelineCivilDate(to);
  if (!start || !end) {
    return null;
  }
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days === 0) {
    return null;
  }
  return `${days > 0 ? '+' : ''}${days}d`;
}

export { isToday };

export function getNextYear(): Date {
  const today = new Date();
  today.setFullYear(today.getFullYear() + 1);
  return today;
}

export function getDuration(start: string, end: string): string {
  return formatDuration(
    intervalToDuration({
      start: parseISO(start),
      end: parseISO(end),
    }),
    {
      locale: es,
    }
  );
}
